((root, factory) => {
  const api = factory(() => root.DIMRuntime, () => typeof module === 'object' && module.exports
    ? require('./dim-scene-identity.js') : root.DIMSceneIdentity);
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.DreamItArtworkProfiles = api;
})(typeof window !== 'undefined' ? window : globalThis, (getRuntime, getSceneIdentity) => {
  'use strict';

  const RESOLUTION_FIELDS = [
    'release_id', 'shopify_variant_id', 'route', 'profile_key', 'profile_version',
    'profile_content_checksum', 'profile_checksum', 'allowed_background_intents', 'editor_output_size_px'
  ];
  const OPTIONAL_GEOMETRY_FIELDS = ['production_artboard_mm', 'ppi', 'editor_initial_placement', 'words_only'];
  const ERROR_FIELDS = ['code', 'message', 'trace_id'];
  const PRODUCT_FIELDS = ['shopify_product_id', 'product_key', 'strategy', 'selection_required', 'anchor_shopify_variant_id'];
  const GENERATION_FIELDS = ['model_snapshot', 'size', 'format', 'quality', 'background'];
  const TTL_MS = 7200000;
  const RESOLUTION_TIMEOUT_MS = 30000;
  const HELPER_TIMEOUT_MS = 180000;
  const IMAGE_TIMEOUT_MS = 330000;
  const STREAM_IDLE_TIMEOUT_MS = 270000;
  // New capabilities are opt-in so the shared Worker continues to serve the
  // unchanged public theme, whose strict response validator predates them.
  const CLIENT_CAPABILITIES = ['editor_initial_placement'];
  const clientCapabilities = () => [...CLIENT_CAPABILITIES,
    ...(getRuntime()?.enabled && getRuntime()?.capabilities?.includes('words-only@1') ? ['words_only'] : [])];
  const IDENTITY_FIELDS = Object.freeze([
    Object.freeze({ key: 'variantId', propertyName: '_dim_shopify_variant_id', resolve: (value) => value.shopify_variant_id }),
    Object.freeze({ key: 'releaseId', propertyName: '_dim_release_id', resolve: (value) => value.release_id }),
    Object.freeze({ key: 'profileKey', propertyName: '_dim_profile_key', resolve: (value) => value.profile_key }),
    Object.freeze({ key: 'profileVersion', propertyName: '_dim_profile_version', resolve: (value) => value.profile_version }),
    Object.freeze({ key: 'profileContentChecksum', propertyName: '_dim_profile_content_checksum', resolve: (value) => value.profile_content_checksum }),
    Object.freeze({ key: 'profileChecksum', propertyName: '_dim_profile_checksum', resolve: (value) => value.profile_checksum }),
    Object.freeze({ key: 'outputWidth', propertyName: '_dim_output_width', resolve: (value) => value.editor_output_size_px.width }),
    Object.freeze({ key: 'outputHeight', propertyName: '_dim_output_height', resolve: (value) => value.editor_output_size_px.height }),
    Object.freeze({ key: 'initialPlacement', propertyName: '_dim_initial_placement', resolve: (value) => value.editor_initial_placement || 'fit' })
  ]);
  const identityKeys = Object.freeze(IDENTITY_FIELDS.map(({ key }) => key));
  const identityPropertyNames = Object.freeze(Object.fromEntries(
    IDENTITY_FIELDS.map(({ key, propertyName }) => [key, propertyName])
  ));
  const profileResolutionRequests = new Map();
  const PROTECTED_PRODUCTION_ORIGINS = new Set([
    `https://${['dream-it-made-dream-it-api', 'andrew-a02', 'workers', 'dev'].join('.')}`
  ]);

  class ArtworkProfileError extends Error {
    constructor(code, message, traceId = '', status = 0, retryAfterSeconds = null) {
      super(message || 'The artwork setup could not be confirmed.');
      this.name = 'ArtworkProfileError';
      this.code = code;
      this.traceId = traceId;
      this.status = status;
      this.retryAfterSeconds = retryAfterSeconds;
    }
  }

  const abortError = (signal) => {
    if (signal?.reason instanceof Error) return signal.reason;
    if (typeof DOMException === 'function') return new DOMException('The operation was aborted.', 'AbortError');
    const error = new Error('The operation was aborted.');
    error.name = 'AbortError';
    return error;
  };

  const timeoutError = (shape) => {
    const profile = shape === 'profile' || shape === 'product';
    const error = new ArtworkProfileError(
      profile ? 'PROFILE_CONFIG_UNAVAILABLE' : 'UPSTREAM_UNAVAILABLE',
      profile
        ? 'The artwork setup is temporarily unavailable. Please try again.'
        : 'The image service is temporarily unavailable. Please try again.'
    );
    error.timeout = true;
    return error;
  };

  const boundedDuration = (value, maximum) => {
    const requested = Number(value);
    return Number.isFinite(requested) && requested > 0 ? Math.min(requested, maximum) : maximum;
  };

  const createRequestBudget = (options = {}, maximumTimeout, createTimeoutError) => {
    const now = Date.now();
    const requestedDeadline = Number(options.deadlineAt);
    const localDeadline = now + boundedDuration(options.timeoutMs, maximumTimeout);
    const deadlineAt = Number.isFinite(requestedDeadline) && requestedDeadline > 0
      ? Math.min(localDeadline, requestedDeadline)
      : localDeadline;
    const controller = new AbortController();
    const parentSignal = options.signal;
    let timeoutId = 0;
    let settled = false;
    let rejectBoundary;
    const boundary = new Promise((_, reject) => { rejectBoundary = reject; });
    // A caller can already be aborted before run() attaches the boundary to its
    // operation. Mark that early rejection handled while preserving it for races.
    boundary.catch(() => {});
    const settleBoundary = (error) => {
      if (settled) return;
      settled = true;
      try { controller.abort(error); } catch (_) { controller.abort(); }
      rejectBoundary(error);
    };
    const handleAbort = () => settleBoundary(abortError(parentSignal));
    if (parentSignal?.aborted) handleAbort();
    else parentSignal?.addEventListener?.('abort', handleAbort, { once: true });
    if (!settled) {
      timeoutId = setTimeout(() => settleBoundary(createTimeoutError()), Math.max(0, Math.ceil(deadlineAt - Date.now())));
    }
    return {
      signal: controller.signal,
      deadlineAt,
      remaining: () => Math.max(0, Math.ceil(deadlineAt - Date.now())),
      run: (operation) => {
        if (parentSignal?.aborted) throw abortError(parentSignal);
        if (deadlineAt <= Date.now()) {
          const error = createTimeoutError();
          try { controller.abort(error); } catch (_) { controller.abort(); }
          throw error;
        }
        return Promise.race([Promise.resolve(operation()), boundary]);
      },
      abort: (error) => {
        if (controller.signal.aborted) return;
        try { controller.abort(error); } catch (_) { controller.abort(); }
      },
      close: () => {
        settled = true;
        clearTimeout(timeoutId);
        parentSignal?.removeEventListener?.('abort', handleAbort);
      }
    };
  };

  const exactKeys = (value, expected) => Boolean(
    value && typeof value === 'object' && !Array.isArray(value)
    && JSON.stringify(Object.keys(value).sort()) === JSON.stringify([...expected].sort())
  );
  const opaque = (value) => typeof value === 'string' && value.length > 0 && value.trim() === value;
  const checksum = (value) => typeof value === 'string' && /^sha256:[0-9a-f]{64}$/.test(value);
  const positiveInteger = (value) => Number.isInteger(value) && value > 0;
  const positiveNumber = (value) => Number.isFinite(Number(value)) && Number(value) > 0;
  const exactAllowedKeys = (value, required, optional = []) => Boolean(
    value && typeof value === 'object' && !Array.isArray(value)
    && required.every((key) => Object.prototype.hasOwnProperty.call(value, key))
    && Object.keys(value).every((key) => required.includes(key) || optional.includes(key))
  );

  const normaliseEndpoint = (value) => {
    if (!opaque(value)) throw new ArtworkProfileError('PROFILE_CONFIG_UNAVAILABLE', 'The artwork service is disabled or unconfigured.');
    let parsed;
    try { parsed = new URL(value); } catch (_) { throw new ArtworkProfileError('PROFILE_CONFIG_UNAVAILABLE', 'The artwork service endpoint is invalid.'); }
    if ((value !== parsed.origin && value !== `${parsed.origin}/`)
      || parsed.protocol !== 'https:' || parsed.username || parsed.password || parsed.search || parsed.hash
      || parsed.pathname !== '/' || parsed.port || !parsed.hostname || parsed.origin === 'null') {
      throw new ArtworkProfileError('PROFILE_CONFIG_UNAVAILABLE', 'The artwork service endpoint is invalid.');
    }
    return parsed.origin;
  };

  const selectEndpoint = (value, environment = 'disabled') => {
    if (!['disabled', 'uat', 'development', 'production'].includes(environment)) {
      throw new ArtworkProfileError('PROFILE_CONFIG_UNAVAILABLE', 'The artwork service environment is invalid.');
    }
    if (environment === 'disabled') {
      throw new ArtworkProfileError('PROFILE_CONFIG_UNAVAILABLE', 'The artwork service is disabled or unconfigured.');
    }
    const endpoint = normaliseEndpoint(value);
    if (['uat', 'development'].includes(environment) && PROTECTED_PRODUCTION_ORIGINS.has(endpoint)) {
      throw new ArtworkProfileError('PROFILE_CONFIG_UNAVAILABLE', 'The UAT artwork service cannot use the production endpoint.');
    }
    return endpoint;
  };

  const normaliseRoute = (route) => {
    if (route === 'create') return 'create_with_ai';
    if (route === 'create_with_ai' || route === 'upload') return route;
    throw new ArtworkProfileError('UNSUPPORTED_ROUTE', 'That artwork route is unavailable.');
  };

  const validateWordsOnlyCapability = (value) => {
    const colour = value => typeof value === 'string' && /^#[0-9A-F]{6}$/.test(value);
    if (!exactKeys(value, ['schemaVersion', 'background', 'inkColours', 'mappingVersion', 'mappingChecksum'])
      || value.schemaVersion !== 'words-only-capability@1'
      || !exactKeys(value.background, ['kind', 'colour']) || value.background.kind !== 'solid' || !colour(value.background.colour)
      || !Array.isArray(value.inkColours) || !value.inkColours.length || value.inkColours.length > 16
      || !value.inkColours.every(colour) || new Set(value.inkColours.map(item => item.toLowerCase())).size !== value.inkColours.length
      || value.inkColours.some(item => item.toLowerCase() === value.background.colour.toLowerCase())
      || !Number.isSafeInteger(value.mappingVersion) || value.mappingVersion < 1 || !checksum(value.mappingChecksum)) {
      throw new ArtworkProfileError('UNEXPECTED_RESPONSE', 'The artwork setup Words-only capability was not recognised.');
    }
    return { schemaVersion: value.schemaVersion, background: { kind: value.background.kind, colour: value.background.colour },
      inkColours: [...value.inkColours], mappingVersion: value.mappingVersion, mappingChecksum: value.mappingChecksum };
  };
  const validateResolution = (value) => {
    if (!exactAllowedKeys(value, RESOLUTION_FIELDS, OPTIONAL_GEOMETRY_FIELDS)) throw new ArtworkProfileError('UNEXPECTED_RESPONSE', 'The artwork setup response was not recognised.');
    const hasArtboard = Object.prototype.hasOwnProperty.call(value, 'production_artboard_mm');
    const hasPpi = Object.prototype.hasOwnProperty.call(value, 'ppi');
    if (!opaque(value.release_id) || !opaque(value.shopify_variant_id) || !['create_with_ai', 'upload'].includes(value.route)
      || !opaque(value.profile_key) || !positiveInteger(value.profile_version)
      || !checksum(value.profile_content_checksum) || !checksum(value.profile_checksum)
      || !Array.isArray(value.allowed_background_intents) || value.allowed_background_intents.some((intent) => !['auto', 'isolated_motif', 'full_background'].includes(intent))
      || !exactKeys(value.editor_output_size_px, ['width', 'height'])
      || !positiveInteger(value.editor_output_size_px.width) || !positiveInteger(value.editor_output_size_px.height)
      || (Object.prototype.hasOwnProperty.call(value, 'editor_initial_placement')
        && !['fit', 'fill'].includes(value.editor_initial_placement))
      || (hasArtboard && (!exactKeys(value.production_artboard_mm, ['width', 'height'])
        || !positiveNumber(value.production_artboard_mm.width) || !positiveNumber(value.production_artboard_mm.height)))
      || (hasPpi && !positiveNumber(value.ppi))) {
      throw new ArtworkProfileError('UNEXPECTED_RESPONSE', 'The artwork setup response was incomplete.');
    }
    if (Object.hasOwn(value, 'words_only')) {
      if (value.route !== 'upload') throw new ArtworkProfileError('UNEXPECTED_RESPONSE', 'The artwork setup Words-only route was not recognised.');
      validateWordsOnlyCapability(value.words_only);
    }
    return structuredClone(value);
  };

  // The paid owner and primary router use a code-only envelope; the underlying
  // image API retains its existing traced envelope. Accept only known pairs,
  // with local customer copy, rather than displaying arbitrary service data.
  const PAID_ERROR_MESSAGES = Object.freeze({
    TURNSTILE_INVALID: [403, 'Please complete a fresh Human Check and try again.'],
    INVALID_REQUEST: [400, 'Please check the request and try again.'],
    INVALID_CONTENT_TYPE: [415, 'The image request format could not be accepted.'],
    REQUEST_TOO_LARGE: [413, 'That request is too large.'],
    RATE_LIMITED: [429, 'You have reached the current limit. Please wait before trying again.'],
    REQUEST_TIMEOUT: [408, 'The image request timed out.'],
    REQUEST_BODY_TIMEOUT: [408, 'The image request could not be received in time.'],
    REQUEST_ABORTED: [408, 'The image request was interrupted.'],
    REQUEST_EXPIRED: [410, 'This image request has expired.'],
    GENERATION_ACTION_EXPIRED: [410, 'This selected idea has expired. Your draft is saved.'],
    INVALID_GENERATION_ACTION: [400, 'This selected idea could not be accepted. Your draft is saved.'],
    REQUEST_BODY_CHANGED: [409, 'This image request no longer matches the selected idea. Your draft is saved.'],
    REQUEST_CONFLICT: [409, 'This image request conflicts with an earlier request.'],
    UNAVAILABLE_SELECTION: [409, 'That product option is no longer available. Please check your product.'],
    PRIMARY_SOURCE_CHANGED: [409, 'The product setup changed. Please check your product again.'],
    STALE_CONTEXT: [409, 'The product setup changed. Please check your product again.'],
    CONTEXT_MISMATCH: [409, 'The product setup changed. Please check your product again.'],
    CONTEXT_EXPIRED: [410, 'The product setup expired. Please check your product again.'],
    PAID_REQUESTS_DISABLED: [403, 'Image creation is currently paused. Your draft is saved.'],
    PROMPT_HELPER_DISABLED: [503, 'Prompt Helper is currently paused. You can still write your own description.'],
    LEDGER_UNAVAILABLE: [503, 'The image request status service is temporarily unavailable.'],
    PAID_OPERATION_UNAVAILABLE: [503, 'The image request service is temporarily unavailable.'],
    PAID_OPERATION_UNCONFIGURED: [503, 'The image request service is not ready.'],
    PRIMARY_RUNTIME_UNAVAILABLE: [503, 'The image request service is temporarily unavailable.']
  });

  const validateError = (payload, response) => {
    let detail = payload?.error;
    if (exactKeys(payload, ['code']) && Object.hasOwn(PAID_ERROR_MESSAGES, payload.code)
      && response.status === PAID_ERROR_MESSAGES[payload.code][0]) {
      detail = { code: payload.code, message: PAID_ERROR_MESSAGES[payload.code][1], trace_id: '' };
    } else if (!exactKeys(payload, ['error']) || !exactKeys(detail, ERROR_FIELDS)
      || !opaque(payload.error.code) || !opaque(payload.error.message) || !opaque(payload.error.trace_id)) {
      throw new ArtworkProfileError('UNEXPECTED_RESPONSE', 'The artwork service returned an unexpected error.', '', response.status);
    }
    const retryHeader = response.headers?.get?.('Retry-After');
    const retryValue = retryHeader && /^\d+$/.test(retryHeader) ? Number(retryHeader) : null;
    const retry = Number.isSafeInteger(retryValue) && retryValue > 0 ? retryValue : null;
    throw new ArtworkProfileError(detail.code, detail.message, detail.trace_id, response.status, retry);
  };

  const parseResponse = async (response, shape) => {
    let payload;
    try { payload = await response.json(); } catch (_) { throw new ArtworkProfileError('UNEXPECTED_RESPONSE', 'The artwork service returned unreadable data.', '', response.status); }
    if (!response.ok) return validateError(payload, response);
    if (shape === 'product') {
      if (!exactKeys(payload, ['product', 'resolution']) || !exactKeys(payload.product, PRODUCT_FIELDS)) throw new ArtworkProfileError('UNEXPECTED_RESPONSE', 'The product setup response was not recognised.');
      const product = payload.product;
      if (!opaque(product.shopify_product_id) || !opaque(product.product_key) || !['single_profile', 'variant_profile'].includes(product.strategy)
        || typeof product.selection_required !== 'boolean') throw new ArtworkProfileError('UNEXPECTED_RESPONSE', 'The product setup response was incomplete.');
      if (product.strategy === 'variant_profile') {
        if (product.selection_required !== true || product.anchor_shopify_variant_id !== null || payload.resolution !== null) throw new ArtworkProfileError('UNEXPECTED_RESPONSE', 'The variant-selection response was inconsistent.');
      } else {
        if (product.selection_required !== false || !opaque(product.anchor_shopify_variant_id)) throw new ArtworkProfileError('UNEXPECTED_RESPONSE', 'The product anchor response was incomplete.');
        payload.resolution = validateResolution(payload.resolution);
        if (payload.resolution.shopify_variant_id !== product.anchor_shopify_variant_id) throw new ArtworkProfileError('UNEXPECTED_RESPONSE', 'The product anchor response was inconsistent.');
      }
      return structuredClone(payload);
    }
    if (shape === 'profile') {
      if (!exactKeys(payload, ['resolution'])) throw new ArtworkProfileError('UNEXPECTED_RESPONSE', 'The profile response was not recognised.');
      return { resolution: validateResolution(payload.resolution) };
    }
    if (shape === 'prompt') {
      if (!exactKeys(payload, ['prompt', 'resolution']) || !opaque(payload.prompt)) throw new ArtworkProfileError('UNEXPECTED_RESPONSE', 'The prompt response was not recognised.');
      return { prompt: payload.prompt, resolution: validateResolution(payload.resolution) };
    }
    if (shape === 'image') {
      if (!exactKeys(payload, ['image_data_url', 'resolution', 'generation']) || typeof payload.image_data_url !== 'string'
        || !payload.image_data_url.startsWith('data:image/png;base64,') || !exactKeys(payload.generation, GENERATION_FIELDS)
        || !opaque(payload.generation.model_snapshot) || !exactKeys(payload.generation.size, ['width', 'height'])
        || !positiveInteger(payload.generation.size.width) || !positiveInteger(payload.generation.size.height)
        || payload.generation.format !== 'png' || !opaque(payload.generation.quality)
        || !['auto', 'transparent', 'opaque'].includes(payload.generation.background)) {
        throw new ArtworkProfileError('UNEXPECTED_RESPONSE', 'The generated-image response was not recognised.');
      }
      return { image_data_url: payload.image_data_url, resolution: validateResolution(payload.resolution), generation: structuredClone(payload.generation) };
    }
    throw new ArtworkProfileError('UNEXPECTED_RESPONSE', 'The artwork response type was not recognised.');
  };

  const reconcilePaidError = async (paidOperation, paidController, budget, error) => {
    if (!paidOperation) return;
    let terminal = false;
    if (!budget.signal.aborted && budget.remaining() > 0) {
      try {
        const result = await budget.run(() => paidOperation.status({ signal: budget.signal }));
        terminal = ['not_dispatched', 'rejected', 'completed', 'completed_unavailable'].includes(result?.status)
          && result.requestToken === paidOperation.requestToken && paidController.pending() === null;
        if (terminal) error.paidRequestStatus = result.status;
      } catch (_) { /* An uncertain status leaves the durable lock in place. */ }
    }
    if (!terminal) {
      paidOperation.uncertain();
      error.paidRequestPending = true;
    }
  };

  // This describes recovery; it never submits or clears an operation. An owned
  // terminal ledger result is the only evidence that an issued request ended.
  const describeGenerationFailure = (error) => {
    const result = (kind, message, retryAllowed = false) => Object.freeze({ kind, message, retryAllowed });
    const code = error?.code;
    const terminal = ['not_dispatched', 'rejected', 'completed', 'completed_unavailable'].includes(error?.paidRequestStatus);
    const completed = ['completed', 'completed_unavailable'].includes(error?.paidRequestStatus);
    if (error?.paidRequestPending || ['PAID_OPERATION_PENDING', 'PAID_OPERATION_IN_PROGRESS', 'PAID_OPERATION_EXECUTING',
      'PAID_OPERATION_UNCERTAIN', 'PAID_OPERATION_STATUS_UNCERTAIN', 'PAID_OPERATION_SUPERSEDED'].includes(code)) {
      return result('unknown', 'The image request may still be running. Check its status before creating another image.');
    }
    if (completed && (!Number.isInteger(error?.status) || error.status < 400 || ['UNEXPECTED_RESPONSE', 'IMAGE_STREAM_INTERRUPTED'].includes(code))) {
      return result('completed-unavailable', 'The image request finished, but its image could not be recovered. Your previous design is saved.');
    }
    if (code === 'TURNSTILE_INVALID') return result('human-check', 'Please complete a fresh Human Check and try again.', true);
    if (code === 'CONTENT_REJECTED') return result('policy', 'This request was declined by the content safety check. Please revise your description and try again.', true);
    if (['INVALID_REQUEST', 'INVALID_CONTENT_TYPE', 'REQUEST_TOO_LARGE'].includes(code)) return result('invalid-request', 'Please check the request and try again.', true);
    if (terminal && Number.isInteger(error?.status) && error.status >= 400 && code === 'UPSTREAM_UNAVAILABLE') {
      return result('confirmed-failure', 'The image service could not complete this request. Your previous design is saved.', true);
    }
    const known = Object.hasOwn(PAID_ERROR_MESSAGES, code) ? PAID_ERROR_MESSAGES[code] : null;
    return result('unavailable', known?.[1] || 'Image creation is temporarily unavailable. Your draft is saved.', terminal && !completed);
  };

  const post = async (endpoint, path, body, shape, fetchImpl = fetch, options = {}) => {
    const safeEndpoint = normaliseEndpoint(endpoint);
    const maximumTimeout = Number(options.maximumTimeoutMs) || RESOLUTION_TIMEOUT_MS;
    const budget = createRequestBudget(options, maximumTimeout, () => timeoutError(shape));
    let paidOperation = null;
    let paidController = null;
    try {
      const runtime = getRuntime();
      if (shape === 'image' && Object.hasOwn(options, 'generationAction') && !runtime?.enabled) {
        throw new ArtworkProfileError('GENERATION_ACTION_UNAVAILABLE', 'This selected idea cannot be generated until the image service is updated. Your draft is saved.');
      }
      if (runtime?.enabled) {
        if (runtime.error || runtime.runtime?.workerOrigin !== safeEndpoint) throw new ArtworkProfileError('PROFILE_CONFIG_UNAVAILABLE', 'The development artwork service is unavailable.');
        // Non-paid resolves use authenticatedFetch, which forces its own current
        // runtime verification. Paid preparation still verifies before issue.
        if (shape === 'prompt' || shape === 'image') await budget.run(() => runtime.verify());
      }
      const send = runtime?.enabled ? runtime.authenticatedFetch : fetchImpl;
      if (runtime?.enabled && ['prompt', 'image'].includes(shape)) {
        if (!runtime.paid) throw new ArtworkProfileError('PAID_OPERATION_UNCONFIGURED', 'The development image service is not ready.');
        paidController = runtime.paid;
        paidOperation = await budget.run(() => runtime.paid.prepare({ path, body, signal: budget.signal,
          ...(shape === 'image' && Object.hasOwn(options, 'generationAction') ? { generationAction: options.generationAction } : {}) }));
      }
      const response = paidOperation ? await budget.run(() => paidOperation.execute({ signal: budget.signal })) : await budget.run(() => send(`${safeEndpoint}${path}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
        credentials: 'same-origin', cache: 'no-store', signal: budget.signal
      }));
      const result = await budget.run(() => parseResponse(response, shape));
      paidOperation?.complete({ status: 'completed' });
      return result;
    } catch (error) {
      await reconcilePaidError(paidOperation, paidController, budget, error);
      budget.abort(error);
      throw error;
    } finally {
      budget.close();
    }
  };

  const resolveProduct = (endpoint, shopifyProductId, route, fetchImpl, options) => post(endpoint, '/v1/product/resolve', {
    shopify_product_id: String(shopifyProductId), route: normaliseRoute(route), client_capabilities: clientCapabilities()
  }, 'product', fetchImpl, { ...options, maximumTimeoutMs: RESOLUTION_TIMEOUT_MS });

  const cloneProfilePayload = (payload) => ({ resolution: structuredClone(payload.resolution) });

  const resolveProfile = (endpoint, shopifyVariantId, route, backgroundIntent = 'auto', fetchImpl, options = {}) => {
    const normalised = normaliseRoute(route);
    const body = { shopify_variant_id: String(shopifyVariantId), route: normalised, client_capabilities: clientCapabilities() };
    if (normalised === 'create_with_ai') body.artwork_background_intent = backgroundIntent;
    const hasRequestBudget = ['signal', 'timeoutMs', 'deadlineAt'].some((key) => Object.hasOwn(options, key));
    if (typeof fetchImpl === 'function' || hasRequestBudget) {
      return post(endpoint, '/v1/profile/resolve', body, 'profile', fetchImpl, { ...options, maximumTimeoutMs: RESOLUTION_TIMEOUT_MS });
    }

    const safeEndpoint = normaliseEndpoint(endpoint);
    const cacheKey = JSON.stringify([safeEndpoint, getRuntime()?.runtime || null, getRuntime()?.context || null, body]);
    const existing = profileResolutionRequests.get(cacheKey);
    if (existing) return existing.then(cloneProfilePayload);

    const request = post(safeEndpoint, '/v1/profile/resolve', body, 'profile', fetch, {
      ...options, maximumTimeoutMs: RESOLUTION_TIMEOUT_MS
    }).finally(() => {
      if (profileResolutionRequests.get(cacheKey) === request) profileResolutionRequests.delete(cacheKey);
    });
    profileResolutionRequests.set(cacheKey, request);
    return request.then(cloneProfilePayload);
  };

  const coachPrompt = (endpoint, shopifyVariantId, idea, turnstileToken, fetchImpl, options) => post(endpoint, '/v1/prompt', {
    shopify_variant_id: String(shopifyVariantId), route: 'create_with_ai', idea, turnstile_token: turnstileToken,
    client_capabilities: CLIENT_CAPABILITIES
  }, 'prompt', fetchImpl, { ...options, maximumTimeoutMs: HELPER_TIMEOUT_MS });

  const generateImage = (endpoint, resolution, prompt, backgroundIntent, turnstileToken, fetchImpl, promptOrigin, options) => {
    const safe = validateResolution(resolution);
    if (promptOrigin !== undefined && !['helper', 'customer'].includes(promptOrigin)) {
      throw new ArtworkProfileError('INVALID_REQUEST', 'The description source was not recognised.');
    }
    return post(endpoint, '/v1/image', {
      shopify_variant_id: safe.shopify_variant_id,
      route: 'create_with_ai',
      prompt,
      ...(options?.guidedIntent ? { generation_mode: 'guided-illustration', guided_intent: structuredClone(options.guidedIntent) } : {}),
      ...(promptOrigin === undefined ? {} : { prompt_origin: promptOrigin }),
      artwork_background_intent: backgroundIntent || 'auto',
      expected_release_id: safe.release_id,
      expected_profile_key: safe.profile_key,
      expected_profile_version: safe.profile_version,
      expected_profile_checksum: safe.profile_checksum,
      turnstile_token: turnstileToken,
      client_capabilities: CLIENT_CAPABILITIES
    }, 'image', fetchImpl, { ...options, maximumTimeoutMs: IMAGE_TIMEOUT_MS });
  };

  // Experimental progress stays outside the ordinary image payload and journey state.
  const generateImageStreaming = async (endpoint, resolution, prompt, backgroundIntent, turnstileToken, options = {}) => {
    const { fetchImpl = fetch, promptOrigin, onPartial, onProgress, signal } = options;
    const safe = validateResolution(resolution);
    if (promptOrigin !== undefined && !['helper', 'customer'].includes(promptOrigin)) {
      throw new ArtworkProfileError('INVALID_REQUEST', 'The description source was not recognised.');
    }
    const startedAt = Date.now();
    const budget = createRequestBudget({
      signal,
      timeoutMs: options.overallTimeoutMs,
      deadlineAt: options.deadlineAt
    }, IMAGE_TIMEOUT_MS, () => {
      const error = new ArtworkProfileError('IMAGE_STREAM_INTERRUPTED', 'The image preview connection stopped before the image finished. Complete the Human Check, then choose Try again to create it without progress previews.');
      error.streamFailureReason = 'overall_timeout';
      return error;
    });
    let reader;
    let accepted = false;
    let metadata = null;
    let completed = null;
    let partialCount = 0;
    let latestPartial = -1;
    let ordinaryFallback = false;
    let paidOperation = null;
    const successorRuntime = getRuntime();
    let streamDiagnostics;
    let clientStage = 'request';
    const diagnosticText = (value) => String(value || '').slice(0, 1200)
      .replace(/sk-[A-Za-z0-9_-]+/g, '[redacted]').replace(/data:image\/[^\s]+/g, '[image omitted]')
      .replace(/[\u0000-\u001f\u007f]/g, ' ');
    const readStreamDiagnostics = (response) => {
      const allowed = {
        stage: ['http_error', 'unexpected_response', 'fetch_failed', 'aborted'],
        code: ['none', 'other', 'unsupported_parameter', 'unsupported_value', 'invalid_value', 'model_not_found',
          'invalid_api_key', 'rate_limit_exceeded', 'insufficient_quota', 'server_error', 'content_policy_violation',
          'moderation_blocked', 'permission_denied', 'access_terminated', 'image_generation_user_error', 'invalid_request_error'],
        param: ['none', 'other', 'model', 'prompt', 'n', 'size', 'quality', 'background', 'output_format', 'moderation', 'stream', 'partial_images']
      };
      const details = {};
      for (const field of ['stage', 'status', 'code', 'param']) {
        const value = response.headers?.get?.(`X-DIM-Stream-${field}`);
        if (field === 'status' ? /^(?:0|[1-5]\d{2})$/.test(value || '') : allowed[field].includes(value)) details[field] = value;
      }
      const upstreamMessage = response.headers?.get?.('X-DIM-Upstream-Message');
      if (upstreamMessage) {
        try {
          // The Worker redacts known secrets before exposing this bounded UAT diagnostic.
          details.upstreamMessage = diagnosticText(decodeURIComponent(upstreamMessage));
        } catch (_) { /* Ignore malformed diagnostic encoding. */ }
      }
      return Object.keys(details).length ? details : undefined;
    };
    const interrupted = (reason = 'incomplete') => {
      const error = new ArtworkProfileError('IMAGE_STREAM_INTERRUPTED', 'The image preview connection stopped before the image finished. Complete the Human Check, then choose Try again to create it without progress previews.');
      error.streamFailureReason = reason;
      return error;
    };
    try {
      const runtime = getRuntime();
      if (Object.hasOwn(options, 'generationAction') && !runtime?.enabled) {
        throw new ArtworkProfileError('GENERATION_ACTION_UNAVAILABLE', 'This selected idea cannot be generated until the image service is updated. Your draft is saved.');
      }
      if (runtime?.enabled) {
        if (runtime.error || normaliseEndpoint(endpoint) !== runtime.runtime?.workerOrigin) throw new ArtworkProfileError('DEV_SERVICE_UNAVAILABLE', 'The development service is unavailable.');
        await budget.run(() => runtime.verify());
      }
      const requestFetch = runtime?.enabled ? runtime.authenticatedFetch : fetchImpl;
      const body = {
          shopify_variant_id: safe.shopify_variant_id, route: 'create_with_ai', prompt,
          ...(options.guidedIntent ? { generation_mode: 'guided-illustration', guided_intent: structuredClone(options.guidedIntent) } : {}),
          ...(promptOrigin === undefined ? {} : { prompt_origin: promptOrigin }),
          artwork_background_intent: backgroundIntent || 'auto', expected_release_id: safe.release_id,
          expected_profile_key: safe.profile_key, expected_profile_version: safe.profile_version,
          expected_profile_checksum: safe.profile_checksum, turnstile_token: turnstileToken,
          client_capabilities: CLIENT_CAPABILITIES
      };
      if (runtime?.enabled) {
        if (!runtime.paid) throw new ArtworkProfileError('PAID_OPERATION_UNCONFIGURED', 'The development image service is not ready.');
        paidOperation = await budget.run(() => runtime.paid.prepare({ path: '/v1/image/stream', body, signal: budget.signal,
          ...(Object.hasOwn(options, 'generationAction') ? { generationAction: options.generationAction } : {}) }));
      }
      const response = paidOperation ? await budget.run(() => paidOperation.execute({ signal: budget.signal })) : await budget.run(() => requestFetch(`${normaliseEndpoint(endpoint)}/v1/image/stream`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        credentials: 'same-origin', cache: 'no-store', signal: budget.signal,
        body: JSON.stringify(body)
      }));
      clientStage = 'response';
      if (!response.ok) {
        streamDiagnostics = readStreamDiagnostics(response);
        try { await budget.run(() => parseResponse(response, 'image')); } catch (error) {
          if (error.code !== 'IMAGE_STREAMING_DISABLED' || runtime?.enabled) throw error;
          // The disabled gate runs before token verification and image rate consumption.
          onProgress?.({ state: 'ordinary', elapsedMs: Date.now() - startedAt });
          const fallback = generateImage(endpoint, safe, prompt, backgroundIntent, turnstileToken, fetchImpl, promptOrigin, {
            signal, deadlineAt: budget.deadlineAt
          });
          // The ordinary request now owns the unchanged absolute deadline.
          ordinaryFallback = true;
          budget.close();
          return fallback;
        }
      }
      accepted = true;
      if (!response.headers?.get?.('Content-Type')?.includes('text/event-stream') || !response.body?.getReader) throw interrupted('response_not_readable_sse');
      reader = response.body.getReader();
      clientStage = 'reading';
      const decoder = new TextDecoder();
      let pending = '';
      const frameParts = [];
      onProgress?.({ state: 'streaming', elapsedMs: 0 });
      const acceptEvent = async (frame) => {
        const data = frame.split(/\r?\n/).filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).replace(/^ /, '')).join('\n');
        if (!data || data === '[DONE]') return;
        let event;
        try { event = JSON.parse(data); } catch (_) { throw interrupted('event_json_invalid'); }
        if (!event || typeof event !== 'object') throw interrupted('event_not_object');
        if (event.type === 'dim.image.metadata') {
          if (metadata || !exactKeys(event, ['type', 'resolution', 'generation'])) throw interrupted('metadata_duplicate_or_shape');
          // Reuse the ordinary response validator; this placeholder is never displayed or returned.
          const checked = await parseResponse({ ok: true, status: 200, json: async () => ({
            image_data_url: 'data:image/png;base64,', resolution: event.resolution, generation: event.generation
          }) }, 'image');
          if (!exactIdentity(safe, checked.resolution)) throw new ArtworkProfileError('STALE_PROFILE', 'The artwork setup changed. Check the product again.');
          metadata = { resolution: checked.resolution, generation: checked.generation };
          clientStage = 'metadata';
          return;
        }
        if (event.type === 'dim.image.error') return validateError({ error: event.error }, { status: 502 });
        if (!['image_generation.partial_image', 'image_generation.completed'].includes(event.type)) return;
        if (!metadata) throw interrupted('image_before_metadata');
        // ASCII base64 whitespace is valid in the ordinary data-URL path too.
        // Removing it preserves the exact decoded image bytes and quality.
        const encodedImage = typeof event.b64_json === 'string' ? event.b64_json.replace(/[\t\n\f\r ]+/g, '') : '';
        if (!encodedImage || !/^[A-Za-z0-9+/]+={0,2}$/.test(encodedImage)) {
          const error = interrupted('image_base64_invalid');
          error.streamEventDetails = { eventType: event.type,
            base64Type: typeof event.b64_json, base64Length: String(typeof event.b64_json === 'string' ? event.b64_json.length : 0),
            base64Whitespace: String(typeof event.b64_json === 'string' && /[\t\n\f\r ]/.test(event.b64_json)),
            base64InvalidAlphabet: String(/[^A-Za-z0-9+/=]/.test(encodedImage)),
            base64Prefix: encodedImage.startsWith('iVBORw0KGgo') ? 'png_signature' : encodedImage.startsWith('data:') ? 'data_url' : 'other' };
          throw error;
        }
        if (event.type === 'image_generation.partial_image') {
          if (!Number.isInteger(event.partial_image_index) || event.partial_image_index < 0) throw interrupted('partial_index_invalid');
          if (event.partial_image_index <= latestPartial) return;
          latestPartial = event.partial_image_index;
          partialCount += 1;
          clientStage = 'partial';
          onPartial?.(`data:image/png;base64,${encodedImage}`, {
            index: latestPartial, partialCount, elapsedMs: Date.now() - startedAt
          });
          return;
        }
        completed = await parseResponse({ ok: true, status: 200, json: async () => ({
          image_data_url: `data:image/png;base64,${encodedImage}`, ...metadata
        }) }, 'image');
        clientStage = 'completed';
        onProgress?.({ state: 'completed', partialCount, elapsedMs: Date.now() - startedAt });
      };
      const readWithIdleBudget = () => {
        let idleTimer = 0;
        const idleDuration = Math.max(1, Math.min(
          boundedDuration(options.idleTimeoutMs, STREAM_IDLE_TIMEOUT_MS),
          budget.remaining()
        ));
        const idle = new Promise((_, reject) => {
          idleTimer = setTimeout(() => reject(interrupted('idle_timeout')), idleDuration);
        });
        return budget.run(() => Promise.race([reader.read(), idle])).finally(() => clearTimeout(idleTimer));
      };
      while (!completed) {
        const { value, done } = await readWithIdleBudget();
        pending += decoder.decode(value || new Uint8Array(), { stream: !done });
        let separator;
        while (!completed && (separator = /\r?\n\r?\n/.exec(pending))) {
          frameParts.push(pending.slice(0, separator.index));
          const frame = frameParts.join('');
          frameParts.length = 0;
          pending = pending.slice(separator.index + separator[0].length);
          await acceptEvent(frame);
        }
        if (done) {
          if (!completed && (frameParts.length || pending.trim())) await acceptEvent(frameParts.join('') + pending);
          break;
        }
        // Only three trailing characters can begin a split SSE separator.
        // Retain completed text fragments without rescanning the growing image.
        if (!completed && pending.length > 3) {
          frameParts.push(pending.slice(0, -3));
          pending = pending.slice(-3);
        }
      }
      if (!completed) throw interrupted('completed_event_missing');
      paidOperation?.complete({ status: 'completed' });
      return completed;
    } catch (error) {
      await reconcilePaidError(paidOperation, successorRuntime?.paid, budget, error);
      budget.abort(error);
      if (successorRuntime?.enabled) {
        error.streamFallbackAvailable = false;
        throw error;
      }
      if (ordinaryFallback) throw error;
      if (signal?.aborted || error?.name === 'AbortError' || error?.code === 'STALE_SELECTION') throw error;
      streamDiagnostics = { ...streamDiagnostics, ...error?.streamEventDetails, clientStage,
        clientFailureReason: error?.streamFailureReason || 'none',
        clientErrorCode: diagnosticText(error?.code), clientErrorName: diagnosticText(error?.name),
        clientErrorMessage: diagnosticText(error?.message), accepted: String(accepted), metadataReceived: String(Boolean(metadata)) };
      error.streamDiagnostics = streamDiagnostics;
      // Never silently submit a second generation after the stream could have started.
      const recoverable = !error?.code || ['IMAGE_STREAM_INTERRUPTED', 'UNEXPECTED_RESPONSE', 'UPSTREAM_UNAVAILABLE'].includes(error.code);
      if (recoverable) {
        const failure = interrupted();
        failure.traceId = error?.traceId || '';
        failure.streamDiagnostics = streamDiagnostics;
        failure.streamFallbackAvailable = true;
        failure.streamAccepted = accepted;
        throw failure;
      }
      throw error;
    } finally {
      if (reader) {
        try { Promise.resolve(reader.cancel()).catch(() => {}); } catch (_) { /* The stream may already be closed. */ }
        try { reader.releaseLock(); } catch (_) { /* Ignore a reader already released by the runtime. */ }
      }
      budget.close();
    }
  };

  const sameProductionProfile = (previous, current) => {
    try {
      const left = validateResolution(previous);
      const right = validateResolution(current);
      return left.profile_key === right.profile_key
        && left.profile_version === right.profile_version
        && left.profile_content_checksum === right.profile_content_checksum
        && left.editor_output_size_px.width === right.editor_output_size_px.width
        && left.editor_output_size_px.height === right.editor_output_size_px.height
        && (left.editor_initial_placement || 'fit') === (right.editor_initial_placement || 'fit')
        && left.route === right.route;
    } catch (_) { return false; }
  };

  const exactIdentity = (previous, current) => sameProductionProfile(previous, current)
    && previous.release_id === current.release_id
    && previous.shopify_variant_id === current.shopify_variant_id
    && previous.profile_checksum === current.profile_checksum;

  // This optional source policy must not invalidate an unchanged image-backed
  // draft when a capable runtime begins advertising Words-only support.
  const exactWordsIdentity = (previous, current) => exactIdentity(previous, current)
    && Boolean(previous.words_only && current.words_only)
    && JSON.stringify(validateWordsOnlyCapability(previous.words_only)) === JSON.stringify(validateWordsOnlyCapability(current.words_only));

  const createExpiry = (createdAt, ttl = TTL_MS) => Number(createdAt) + Number(ttl);
  const isUnexpired = (record, now = Date.now()) => {
    if (record?.composition !== undefined) {
      try {
        const originalAbsent = record.version === 7 ? record.originalImageKey === '' && record.originalRevision === 0
          : record.source === 'edited' && record.parentImageKey === '';
        if (!originalAbsent) return false;
        const composition = getSceneIdentity().validateComposition(record.composition, { resolution: record.resolution,
          originalImageKey: '', originalRevision: 0, now: Number(now) });
        return Number.isSafeInteger(record.createdAt) && record.createdAt > 0 && record.createdAt <= now
          && Number.isSafeInteger(record.expiresAt) && record.expiresAt > now && record.createdAt < record.expiresAt
          && record.expiresAt <= composition.expiresAt;
      } catch (_) { return false; }
    }
    return Boolean(record && Number(record.createdAt) > 0 && Number(record.expiresAt) === createExpiry(record.createdAt) && Number(record.expiresAt) > Number(now));
  };

  const identityProperties = (resolution) => {
    const value = validateResolution(resolution);
    return Object.fromEntries(IDENTITY_FIELDS.map(({ key, resolve }) => [key, resolve(value)]));
  };

  return Object.freeze({
    ArtworkProfileError, TTL_MS, normaliseRoute, normaliseEndpoint, selectEndpoint, validateResolution, validateWordsOnlyCapability, parseResponse, describeGenerationFailure, resolveProduct,
    resolveProfile, coachPrompt, generateImage, generateImageStreaming, sameProductionProfile, exactIdentity, exactWordsIdentity, createExpiry,
    isUnexpired, identityKeys, identityPropertyNames, identityProperties
  });
});
